import { RouteStop } from "@/reducers/routes2gSlice"
import { AlarmClock, Bed, Clock, Gauge, MapPin, Truck } from "lucide-react"
import { formatDuration, formatUserFriendlyDate } from "./RouteStopView"
import { Location } from "@/reducers/locationsSlice"
import { assumedRestLabelOf } from "@/lib/stopStatus"
import { useTranslation } from "react-i18next"

export const HOVER_OPEN_DELAY = 500
export const HOVER_CLOSE_DELAY = 100

export type RouteDetailsPopoverProps = {
  routeStop: RouteStop
  /** The truck's current speed, shown above the stop when given. */
  speedLabel?: string
}

const getCoordinates = (location: Location) => {
  if (location.lat && location.lon) {
    const lat = parseFloat(location.lat).toFixed(5)
    const lon = parseFloat(location.lon).toFixed(5)
    return `${lat}, ${lon}`
  }
  return undefined
}

const getAddress = (stop: RouteStop) => {
  if (stop.location.formattedAddress) {
    return stop.location.formattedAddress
  } else if (stop.location.countryCode && stop.location.locality) {
    return `${stop.location.countryCode}, ${stop.location.postalCode ? stop.location.postalCode : ""} ${stop.location.locality}`
  }
  return ""
}

const getRemainingTimeWithBreaks = (routeStop: RouteStop) => {
  if (!routeStop.estimated_arrival_time_with_tacho) {
    return undefined
  }
  return Math.max(
    Math.floor(
      new Date(routeStop.estimated_arrival_time_with_tacho).getTime() -
        new Date().getTime(),
    ) / 1000,
    0,
  )
}

const getRemainingTime = (routeStop: RouteStop) => {
  if (!routeStop.estimated_arrival_time) {
    return 0
  }

  return Math.max(
    Math.floor(
      new Date(routeStop.estimated_arrival_time).getTime() -
        new Date().getTime(),
    ) / 1000,
    0,
  )
}

const SpeedRow = ({ speedLabel }: { speedLabel: string }) => {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0">
        <Gauge className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex items-center gap-2">
        <span className="font-medium">{speedLabel}</span>
        <span className="text-muted-foreground">
          {t("maps.routeStopDetailPopover.speed")}
        </span>
      </div>
    </div>
  )
}

/** For a chat row whose truck has no route: the speed is all there is to show. */
export const CarSpeedPopover = ({ speedLabel }: { speedLabel: string }) => (
  <div onClick={(e) => e.stopPropagation()} className="w-full p-4 text-sm">
    <SpeedRow speedLabel={speedLabel} />
  </div>
)

export default function RouteStopDetailsPopover({
  routeStop,
  speedLabel,
}: RouteDetailsPopoverProps) {
  const { t } = useTranslation()
  return (
    <div onClick={(e) => e.stopPropagation()} className="w-full p-4 text-sm">
      <div className="space-y-2">
        {speedLabel && <SpeedRow speedLabel={speedLabel} />}

        {routeStop.scheduled_arrival_time && (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <AlarmClock className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {formatUserFriendlyDate(
                  new Date(routeStop.scheduled_arrival_time),
                )}
              </span>
              <span className="text-muted-foreground">
                {t("maps.routeStopDetailPopover.scheduledArrival")}
              </span>
            </div>
          </div>
        )}

        {!!routeStop.remaining_time && (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {formatDuration(getRemainingTime(routeStop))}
              </span>
              <span className="text-muted-foreground">
                {t("maps.routeStopDetailPopover.driving")}
              </span>
            </div>
          </div>
        )}

        {!!routeStop.remaining_time &&
          !!getRemainingTimeWithBreaks(routeStop) && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <Truck className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {formatDuration(
                    getRemainingTimeWithBreaks(routeStop) as number,
                  )}
                </span>
                <span className="text-muted-foreground">
                  {t("maps.routeStopDetailPopover.withBreaks")}
                </span>
              </div>
            </div>
          )}

        {assumedRestLabelOf(routeStop) && (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Bed className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-muted-foreground">
              {assumedRestLabelOf(routeStop)}
            </span>
          </div>
        )}

        {/* Separator */}
        {(speedLabel ||
          routeStop.remaining_time ||
          routeStop.scheduled_arrival_time) && (
          <div className="my-3 border-t border-border"></div>
        )}

        {/* Address */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 pt-1">
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            {routeStop.location.companyName?.trim() && (
              <span className="font-semibold" data-testid="stop-company-name">
                {routeStop.location.companyName.trim()}
              </span>
            )}
            <span className="font-medium">{getAddress(routeStop)}</span>
            <span className="mt-1 text-muted-foreground">
              {getCoordinates(routeStop.location)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
